import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { useDocsPosts, useDocsCategories } from '@/hooks/useWordPressDocs';
import { docsService } from '@/services/docsService';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import SEO from '@/components/SEO';

const SupportAll = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  
  const { data: categories } = useDocsCategories();
  const { data: postsData, isLoading } = useDocsPosts({
    per_page: 50,
    search: searchQuery || undefined,
    doc_category: selectedCategory || undefined,
  });

  const handlePostClick = (post: any) => {
    navigate(`/support/article/${post.slug}`);
  };

  const estimateReadingTime = (content: string) => {
    const wordsPerMinute = 200;
    const words = docsService.stripHtml(content).split(' ').length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min read`;
  };

  const getCategoryName = (categoryId: number) => {
    return categories?.find(cat => cat.id === categoryId)?.name || '';
  };

  return (
    <>
      <SEO 
        title="All Resources - Support - SeniorThrive"
        description="Browse all support resources, guides, and documentation for SeniorThrive"
      />
      
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-muted/30 py-12">
          <div className="container mx-auto px-4">
            <Link 
              to="/support" 
              className="inline-flex items-center text-primary hover:underline mb-6"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Support Center
            </Link>
            
            <h1 className="text-4xl font-bold text-foreground mb-4">
              All Support Resources
            </h1>
            <p className="text-lg text-muted-foreground">
              Find guides, tutorials, and documentation to help you get the most out of SeniorThrive.
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full md:w-64">
                <div className="flex items-center">
                  <FunnelIcon className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="All Categories" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Categories</SelectItem>
                {categories?.map((category) => (
                  <SelectItem key={category.id} value={category.id.toString()}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Results */}
          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 9 }).map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader className="space-y-2">
                    <div className="h-4 bg-muted rounded w-3/4"></div>
                    <div className="h-6 bg-muted rounded w-full"></div>
                    <div className="h-4 bg-muted rounded w-5/6"></div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="h-3 bg-muted rounded w-full"></div>
                      <div className="h-3 bg-muted rounded w-3/4"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : postsData?.posts.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-xl font-semibold mb-2">No Resources Found</h3>
              <p className="text-muted-foreground mb-6">
                Try adjusting your search terms or category filter.
              </p>
              <button 
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('');
                }}
                className="text-primary hover:underline"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <p className="text-muted-foreground">
                  Showing {postsData?.posts.length} of {postsData?.total} resources
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {postsData?.posts.map((post) => {
                  const readingTime = estimateReadingTime(post.content.rendered);
                  const publishDate = new Date(post.date).toLocaleDateString();
                  const primaryCategory = post.categories[0];
                  
                  return (
                    <Card 
                      key={post.id}
                      className="hover:shadow-lg transition-shadow cursor-pointer group"
                      onClick={() => handlePostClick(post)}
                    >
                      <CardHeader>
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant="outline" className="text-xs">
                            {getCategoryName(primaryCategory)}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {readingTime}
                          </span>
                        </div>
                        <CardTitle className="group-hover:text-primary transition-colors">
                          {docsService.stripHtml(post.title.rendered)}
                        </CardTitle>
                        <CardDescription>
                          {docsService.stripHtml(post.excerpt.rendered).substring(0, 150)}...
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="text-xs text-muted-foreground">
                          Published {publishDate}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SupportAll;